import type { MenuItem, NavMenu } from '@/constants/navigation/types';

export type PermissionActionKey = 'view' | 'create' | 'update' | 'delete' | 'approve';
export type PermissionMap = Record<string, Partial<Record<PermissionActionKey, boolean>>>;

const PUBLIC_ROUTE_PREFIXES = ['/trang-chu', '/xac-thuc'];
const ALWAYS_VISIBLE_MENU_HREFS = new Set<string>([]);

const toApiKeySegment = (value: string) => value.replace(/-/g, '_');

export const getPermissionResourceKeyFromPath = (pathname: string) => {
  const parts = pathname
    .split('?')[0]
    .split('#')[0]
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean);

  if (parts.length === 0) return '';

  const moduleKey = toApiKeySegment(parts[0]);
  const resourceKey = toApiKeySegment(parts.at(-1) ?? parts[0]);
  return `${moduleKey}.${resourceKey}`;
};

export const isPublicPermissionPath = (pathname: string) =>
  PUBLIC_ROUTE_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`));

export const canViewPath = (pathname: string, permissions: PermissionMap, isAdmin = false) => {
  if (isAdmin || isPublicPermissionPath(pathname)) return true;

  const resourceKey = getPermissionResourceKeyFromPath(pathname);
  if (!resourceKey) return false;

  return permissions[resourceKey]?.view === true;
};

const filterMenuItems = (items: MenuItem[], permissions: PermissionMap, isAdmin: boolean): MenuItem[] =>
  items
    .map(item => {
      const filteredChildren = item.items ? filterMenuItems(item.items, permissions, isAdmin) : undefined;

      const canViewSelf = item.href
        ? ALWAYS_VISIBLE_MENU_HREFS.has(item.href) || canViewPath(item.href, permissions, isAdmin)
        : false;

      const isVisible = filteredChildren ? filteredChildren.length > 0 || canViewSelf : canViewSelf;

      if (!isVisible) return null;

      return {
        ...item,
        ...(filteredChildren ? { items: filteredChildren } : {})
      };
    })
    .filter(Boolean) as MenuItem[];

export const filterMenusByPermissions = (menus: NavMenu[], permissions: PermissionMap, isAdmin = false): NavMenu[] =>
  menus
    .map(menu => ({
      ...menu,
      items: filterMenuItems(menu.items, permissions, isAdmin)
    }))
    .filter(menu => menu.items.length > 0);
